import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../firebaseConfig';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import './index.css';

const OwnerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changeEmail, setChangeEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.role;
            if (role === 'owner') {
              navigate('/owner-dashboard', { replace: true });
            } else {
              setError('You are not authorized to access the owner dashboard.');
              await auth.signOut();
            }
          } else {
            setError('User data not found. Please contact admin.');
            await auth.signOut();
          }
        } catch (error) {
          setError('An error occurred while checking your role.');
          await auth.signOut();
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const role = userData.role;
        if (role === 'owner') {
          navigate('/owner-dashboard', { replace: true });
        } else {
          setError('You are not authorized to access the owner dashboard.');
          await auth.signOut();
        }
      } else {
        setError('User data not found. Please contact admin.');
        await auth.signOut();
      }
    } catch (error) {
      let errorMessage = 'Invalid email or password.';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid credentials. Please check your email and password.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setChangeError('');
    setChangeSuccess('');

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in.');
      }
      const credential = EmailAuthProvider.credential(changeEmail, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setChangeSuccess('Password updated successfully!');
      setChangeEmail('');
      setCurrentPassword('');
      setNewPassword('');
      setShowChangePassword(false);
    } catch (error) {
      let errorMessage = 'Failed to change password. Please try again.';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/user-mismatch') {
        errorMessage = 'Email does not match the current user.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak. Use at least 6 characters.';
      }
      setChangeError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="owner-login-container">
      <div className="owner-login-card">
        <h2>{showChangePassword ? 'Change Password' : 'Owner Login'}</h2>
        {error && <div className="error-message">{error}</div>}
        {changeSuccess && <div className="success-message">{changeSuccess}</div>}
        {changeError && <div className="error-message">{changeError}</div>}
        {!showChangePassword ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="password">Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Login'}
            </button>
            <div className="text-center mt-4">
              <span
                className="text-link"
                onClick={() => {
                  setShowChangePassword(true);
                  setError('');
                  setChangeSuccess('');
                  setChangeError('');
                }}
              >
                Change Password
              </span>
            </div>
          </form>
        ) : (
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label htmlFor="changeEmail">Email Address</label>
              <input
                type="email"
                id="changeEmail"
                value={changeEmail}
                onChange={(e) => setChangeEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Enter current password"
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <div className="form-group password-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Update Password'}
            </button>
            <div className="text-center mt-4">
              <span
                className="text-link"
                onClick={() => {
                  setShowChangePassword(false);
                  setChangeEmail('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setChangeError('');
                  setChangeSuccess('');
                }}
              >
                Back to Login
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OwnerLogin;